import React, { useRef } from "react";
import styled from "styled-components";
import { RotatingLines } from "react-loader-spinner";
import { Maximize2, Minimize2 } from "lucide-react";
import { ReactComponent as CloseCall } from "../../Assets/iconsv2/controlButtons/closeCall.svg";
import { useTranslate } from "@tolgee/react";
import { darkTheme } from "../../Assets/styles/darkTheme";
import { useAuthService } from "../../Context/AuthContext";
import useOrientationLayout from "../../hooks/useOrientationLayout";
import CallChatPanel from "../CallChatComponents/CallChatPanel";
import CallOptionModal from "./CallOptionModal";
import PollPanel from "../Conferences/PollPanel";
import MobileTabBar from "./MobileTabBar";
import VoiceControlsPanel from "./VoiceControlsPanel";
import TranslationFeedPanel, { SpeakTurnControl, useSpeakTurnQueue } from "./TranslationFeedPanel";

const noOp = () => Promise.resolve();

const FeedTabContent = ({ hidden, ...props }) => {
  const speakTurn = useSpeakTurnQueue({
    speakersQueue: props.speakersQueue,
    canRequestSpeak: props.canRequestSpeak,
    translationType: props.translationType,
    speakStatus: props.speakStatus,
    setSpeakStatus: props.setSpeakStatus,
    handleSendCancelSpeakMessage: props.handleSendCancelSpeakMessage,
    handleRequestSpeakMessage: props.handleRequestFloor,
    requestSpeakPayload: null,
    muteMicrophone: noOp,
    unmuteMicrophone: noOp,
    micEnabledRef: props.micEnabledRef,
    isLeavingCallRef: props.isLeavingCallRef,
    room: null,
    localParticipant: null,
  });

  return (
    <HiddenableWrapper $hidden={hidden}>
      <TranslationFeedPanel
        speechRecongnitionMessages={props.speechRecongnitionMessages}
        useRealtimeApi={false}
        realtimeTranslations={[]}
        compact
      />
      <SpeakTurnControl
        translationType={props.translationType}
        canRequestSpeak={props.canRequestSpeak}
        speakStatus={props.speakStatus}
        ahedUsers={speakTurn.ahedUsers}
        handleRequestSpeak={speakTurn.handleRequestSpeak}
        handleStopSpeakingWithoutSend={speakTurn.handleStopSpeakingWithoutSend}
        handleStopSpeaking={speakTurn.handleStopSpeaking}
      />
    </HiddenableWrapper>
  );
};

const ConferenceBroadcastMobileLayout = ({
  conferenceTitle,
  durationText,
  videoRef,
  ttsAudioRef,
  playbackUrl,
  streamLoading,
  streamReconnecting,
  isProvisioning,
  needsSoundGesture,
  enableSound,
  reloadPlayers,
  speakersQueue,
  isOptionModalOpen,
  handleOpenOptionModal,
  handleCloseOptionModal,
  audioInputDevice,
  audioOutputDevice,
  videoInputDevice,
  setSelectedAudioInput,
  setSelectedAudioOutput,
  setSelectedVideoInput,
  activePanel,
  chatAllowed,
  pollsAllowed,
  translationActive,
  unreadChatCount,
  activePollBadge,
  handleChatBtn,
  handlePollsBtn,
  handleFeedBtn,
  handleAudioBtn,
  resetCall,
  data,
  activeCallRef,
  callChatConversation,
  onConversationCreated,
  onChatOpened,
  conferenceId,
  addObserver,
  removeObserver,
  translationType,
  speechRecongnitionMessages,
  speakStatus,
  setSpeakStatus,
  handleSendCancelSpeakMessage,
  handleRequestFloor,
  micEnabledRef,
  isLeavingCallRef,
  canRequestSpeak,
  setEmotiiVoice,
  emotiiVoice,
  incomingVolume,
  dimEnabled,
  emotiiVolume,
  handleIncomingVolumeChange,
  setEmotiiVolume,
}) => {
  const { t } = useTranslate();
  const { user } = useAuthService();
  const { isLandscape, isFullscreenActive, enterFullscreenLandscape, exitFullscreenLandscape } =
    useOrientationLayout();
  const wrapperRef = useRef(null);

  const onSelectTab = (tab) => {
    if (tab === "chat") handleChatBtn();
    else if (tab === "polls") handlePollsBtn();
    else if (tab === "feed") handleFeedBtn();
    else if (tab === "audio") handleAudioBtn();
  };

  const handleFullscreenToggle = async () => {
    if (isFullscreenActive) {
      exitFullscreenLandscape();
      return;
    }
    await enterFullscreenLandscape(wrapperRef);
  };

  return (
    <MobileWrapper ref={wrapperRef} $isLandscape={isLandscape}>
      {isOptionModalOpen && (
        <CallOptionModal
          onClose={handleCloseOptionModal}
          audioInputDevice={audioInputDevice}
          audioOutputDevice={audioOutputDevice}
          videoInputDevice={videoInputDevice}
          micAllowed={false}
          cameraAllowed={false}
          isAttendee={true}
          isMobile={true}
          setSelectedAudioInput={setSelectedAudioInput}
          setSelectedAudioOutput={setSelectedAudioOutput}
          setSelectedVideoInput={setSelectedVideoInput}
          isConference={true}
        />
      )}

      <VideoSection $isLandscape={isLandscape}>
        <HeaderContainer>
          <NameWrapper>
            <Name>{conferenceTitle}</Name>
            <DurationWrapper>
              <PulsingDot />
              <Duration>{durationText}</Duration>
            </DurationWrapper>
          </NameWrapper>
          {isLandscape && (
            <HeaderDisconnectButton
              type="button"
              onClick={resetCall}
              title={t("conferences.disconnect")}
              aria-label={t("conferences.disconnect")}
            >
              <CloseCall />
            </HeaderDisconnectButton>
          )}
        </HeaderContainer>

        <StreamArea>
          {(!playbackUrl || streamLoading) && (
            <WaitingOverlay>
              <RotatingLines
                strokeColor={darkTheme.menu.loadingIndicator}
                strokeWidth="3"
                animationDuration="0.75"
                width="48"
                visible
              />
              <WaitingText>
                {streamReconnecting
                  ? t("conferences.broadcast_buffering") || "Buffering…"
                  : isProvisioning
                  ? t("conferences.broadcast_provisioning") || "Preparing your stream…"
                  : t("conferences.broadcast_starting") || "Broadcast starting…"}
              </WaitingText>
            </WaitingOverlay>
          )}

          <StyledVideo
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{ display: playbackUrl && !streamLoading ? "block" : "none" }}
          />

          <audio ref={ttsAudioRef} autoPlay muted playsInline style={{ display: "none" }} />

          {playbackUrl && (
            <ReloadButton
              type="button"
              onClick={reloadPlayers}
              title={t("conferences.refresh_stream") || "Refresh stream"}
              aria-label={t("conferences.refresh_stream") || "Refresh stream"}
            >
              ↻
            </ReloadButton>
          )}

          {needsSoundGesture && playbackUrl && !streamLoading && (
            <SoundPill type="button" onClick={enableSound}>
              🔊 {t("conferences.tap_for_sound") || "Tap for sound"}
            </SoundPill>
          )}

          {playbackUrl && (
            <FullscreenButton
              type="button"
              onClick={handleFullscreenToggle}
              title={t("conferences.toggle_fullscreen") || "Toggle fullscreen"}
              aria-label={t("conferences.toggle_fullscreen") || "Toggle fullscreen"}
            >
              {isFullscreenActive ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </FullscreenButton>
          )}

          {speakersQueue?.length > 0 && (
            <QueueBadge>
              {speakersQueue.map((speaker, idx) => (
                <QueueItem key={speaker.id ?? idx}>
                  <QueueDot $isYou={speaker.id === user?.id} />
                  {speaker.name || speaker.id}
                  {speaker.id === user?.id && (
                    <YouLabel> ({t("call_modal.you") || "you"})</YouLabel>
                  )}
                </QueueItem>
              ))}
            </QueueBadge>
          )}
        </StreamArea>
      </VideoSection>

      <PanelSection $isLandscape={isLandscape}>
        <ContentArea>
          {translationActive && (
            <FeedTabContent
              hidden={activePanel !== "feed"}
              speakersQueue={speakersQueue}
              canRequestSpeak={canRequestSpeak}
              translationType={translationType}
              speakStatus={speakStatus}
              setSpeakStatus={setSpeakStatus}
              handleSendCancelSpeakMessage={handleSendCancelSpeakMessage}
              handleRequestFloor={handleRequestFloor}
              micEnabledRef={micEnabledRef}
              isLeavingCallRef={isLeavingCallRef}
              speechRecongnitionMessages={speechRecongnitionMessages}
            />
          )}

          {translationActive && (
            <HiddenableWrapper $hidden={activePanel !== "audio"}>
              <VoiceControlsPanel
                standalone
                incomingVolume={incomingVolume}
                dimEnabled={dimEnabled}
                emotiiVolume={emotiiVolume}
                onIncomingVolumeChange={handleIncomingVolumeChange}
                onEmotiiVolumeChange={setEmotiiVolume}
                setEmotiiVoice={setEmotiiVoice}
                emotiiVoice={emotiiVoice}
                isConference={true}
                isConferenceAttendee={true}
                activeCallRef={activeCallRef}
              />
            </HiddenableWrapper>
          )}

          {chatAllowed && activePanel === "chat" && (
            <CallChatPanel
              fullWidth
              callInfo={data}
              onClose={() => handleChatBtn()}
              activeCallRef={activeCallRef}
              existingConversation={callChatConversation}
              onConversationCreated={onConversationCreated}
              onMessagesRead={() => onChatOpened?.()}
            />
          )}

          {pollsAllowed && activePanel === "polls" && (
            <PollPanel
              fullWidth
              conferenceId={conferenceId}
              isOrganizer={false}
              isPresenter={false}
              onClose={() => handlePollsBtn()}
              addObserver={addObserver}
              removeObserver={removeObserver}
            />
          )}
        </ContentArea>

        <MobileTabBar
          activeTab={activePanel}
          onSelectTab={onSelectTab}
          chatAllowed={chatAllowed}
          pollsAllowed={pollsAllowed}
          translationActive={translationActive}
          unreadChatCount={unreadChatCount}
          activePollBadge={activePollBadge}
          onOpenSettings={handleOpenOptionModal}
          onDisconnect={resetCall}
          showDisconnect={!isLandscape}
        />
      </PanelSection>
    </MobileWrapper>
  );
};

export default ConferenceBroadcastMobileLayout;

const MobileWrapper = styled.div`
  display: flex;
  flex-direction: ${({ $isLandscape }) => $isLandscape ? "row" : "column"};
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.menu.background};
  position: absolute;
  inset: 0;
  z-index: 20;
  overflow: hidden;
`;

const VideoSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: ${({ $isLandscape }) => $isLandscape ? "0 0 65%" : "0 0 40%"};
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

const PanelSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const HiddenableWrapper = styled.div`
  display: ${({ $hidden }) => $hidden ? "none" : "flex"};
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 10px 16px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  overflow: hidden;
  flex-shrink: 0;
`;

const HeaderDisconnectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const Name = styled.h3`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
`;

const DurationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PulsingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
`;

const Duration = styled.p`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  opacity: 0.7;
  margin: 0;
  letter-spacing: 0.3px;
`;

const StreamArea = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const WaitingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const WaitingText = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  margin: 0;
  opacity: 0.7;
`;

const ReloadButton = styled.button`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(24, 29, 36, 0.85);
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FullscreenButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(24, 29, 36, 0.85);
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SoundPill = styled.button`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 999px;
  background: rgba(24, 29, 36, 0.9);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  z-index: 6;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
`;

const QueueBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(24, 29, 36, 0.85);
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 160px;
  z-index: 5;
`;

const QueueItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #fff;
`;

const QueueDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $isYou }) => ($isYou ? "#7069fd" : "rgba(255,255,255,0.5)")};
`;

const YouLabel = styled.span`
  font-size: 11px;
  color: #7069fd;
`;
